import React, { useState, useEffect } from "react";
import {
    getProgramListByEmployer, getProgramList, getProgramListByPartner, fetchAttendanceReportByProgramId
} from "lib/axiosUtils";
import Spinner from 'components/Spinner';
import Select from 'react-select';
import ExcelJS from "exceljs";
import moment from 'moment';
import { saveAs } from "file-saver";
import { EXPORT_ICON } from "assets/constants/Icons";

const AttendanceReportPage = () => {
    const [projectListData, setProjectListData] = useState([]);
    const [loadingComplete, setLoadingComplete] = useState(false);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [projectListComponent, setProjectListComponent] = useState([]);
    const [attendanceReport, setAttendanceReport] = useState([]);
    const [totalWeeks, setTotalWeeks] = useState([]);
    const [metaData] = useState(JSON.parse(localStorage.getItem("metadata")) ? JSON.parse(localStorage.getItem("metadata")) : []);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState([]);
    const [partnerListComponent, setPartnerListComponent] = useState([]);
    const [filteredAttendanceReport, setFilteredAttendanceReport] = useState([]);

    useEffect(() => {
        const fetchProjectList = async () => {
            try {
                const userDetail = JSON.parse(localStorage.getItem("userData"));
                let response;
                if (userDetail?.employer_id) {
                    response = await getProgramListByEmployer({}, {}, userDetail.employer_id);
                } else if (userDetail?.partner_id) {
                    response = await getProgramListByPartner({}, {}, userDetail.partner_id);
                } else {
                    response = await getProgramList({}, {});
                }

                const projectComponentList = response.data.result.map((item) => {
                    return ({ label: item.program_name, value: item._id });
                });
                setProjectListComponent(projectComponentList);
                setProjectListData(response.data.result);
            } catch (error) {
                console.error("Error while fetching Partner Data:", error);
            } finally {
                setLoadingComplete(true);
            }
        };
        fetchProjectList();
    }, []);

    useEffect(() => {
        if (metaData.length > 0 && metaData[0].meta_data && metaData[0].meta_data.attendance_week_list.length > 0) {
            setTotalWeeks(metaData[0].meta_data.attendance_week_list);
        }
    }, [metaData]);

    useEffect(() => {
        if (selectedProgram) {
            fetchReportData()
        }
        async function fetchReportData() {
            setLoadingComplete(false);
            try {
                const attendanceReportResult = await fetchAttendanceReportByProgramId({}, {}, selectedProgram.value);
                setAttendanceReport(attendanceReportResult.data.result);
                setFilteredAttendanceReport(attendanceReportResult.data.result);  // default (no partner filter)
            }
            catch (error) {
                console.log("Error occurred while fetching the attendance report", error);
            }
            finally {
                setLoadingComplete(true);
            }
        }
    }, [selectedProgram])

    useEffect(() => {
        if (!selectedPartner?.value) {
            setFilteredAttendanceReport(attendanceReport);
            return;
        }
        const filteredData = attendanceReport.filter(
            item => item.partner_id === selectedPartner.value
        );
        setFilteredAttendanceReport(filteredData);
    }, [selectedPartner, attendanceReport]);

    useEffect(() => {
        async function fetchData() {
            const selectedProgramData = projectListData.find((findItem) => findItem._id === selectedProgram?.value);
            const partnerList = selectedProgramData?.partnerItem.map((item) => {
                return {
                    label: item.training_partner,
                    value: item._id
                }
            })
            setPartnerListComponent(partnerList)
        }
        fetchData();
    }, [selectedPartner, projectListData,selectedProgram]);

    const changeProjectName = (event) => {
        setSelectedProgram(event);
        setSelectedPartner(null);
    }

    const changePartnerData = (event) => {
        setSelectedPartner(event);
    }

    const exportToExcel = async () => {
        setDownloadLoading(true);
        try {
            let data = filteredAttendanceReport.map((item, index) => {
                item.sr_no = index + 1;
                item.candidate_name = `${item.first_name} ${item.last_name}`;
                item.Week_1 = item.week_1 && item.week_1.percentage ? `${item.week_1.percentage} %` : "";
                item.Week_2 = item.week_2 && item.week_2.percentage ? `${item.week_2.percentage} %` : "";
                item.Week_3 = item.week_3 && item.week_3.percentage ? `${item.week_3.percentage} %` : "";
                item.Week_4 = item.week_4 && item.week_4.percentage ? `${item.week_4.percentage} %` : "";
                item.Week_5 = item.week_5 && item.week_5.percentage ? `${item.week_5.percentage} %` : "";
                item.Week_6 = item.week_6 && item.week_6.percentage ? `${item.week_6.percentage} %` : "";
                item.Week_7 = item.week_7 && item.week_7.percentage ? `${item.week_7.percentage} %` : "";
                item.Week_8 = item.week_8 && item.week_8.percentage ? `${item.week_8.percentage} %` : "";
                item.Week_9 = item.week_9 && item.week_9.percentage ? `${item.week_9.percentage} %` : "";
                item.total_percentage = item.totalPresentPercentage ? `${item.totalPresentPercentage} %` : "";
                return item;
            })
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Attendance Report");
            const columns = [
                { header: "Sr. No", key: "sr_no" },
                { header: "Candidate Name", key: "candidate_name" },
                { header: "Contact No", key: "mobile_no" },
                { header: "Training partner", key: "training_partner" },
                { header: "Employer Unit", key: "employer_unit" },
                { header: "Week-1", key: "Week_1" },
                { header: "Week-2", key: "Week_2" },
                { header: "Week-3", key: "Week_3" },
                { header: "Week-4", key: "Week_4" },
                { header: "Week-5", key: "Week_5" },
                { header: "Week-6", key: "Week_6" },
                { header: "Week-7", key: "Week_7" },
                { header: "Week-8", key: "Week_8" },
                { header: "Week-9", key: "Week_9" },
                { header: "Total", key: "total_percentage" }
            ];
            const mainHeaderRow = worksheet.addRow(columns.map((column) => column.header));
            mainHeaderRow.eachCell((cell, colNumber) => {
                cell.font = { bold: true };
                cell.alignment = { vertical: 'middle', horizontal: 'left' };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: colNumber < 6 ? "fffdd8ff" : "ffe4eff9" || 'FFFFFF' },
                };
                cell.border = {
                    top: { style: 'thin', color: { argb: '000000' } },
                    left: { style: 'thin', color: { argb: '000000' } },
                    bottom: { style: 'thin', color: { argb: '000000' } },
                    right: { style: 'thin', color: { argb: '000000' } },
                };
            });
            data.forEach((row, dataIndex) => {
                const dataRow = [];
                columns.forEach((column) => {
                    dataRow.push(row[column.key]);
                });
                const newRow = worksheet.addRow(dataRow);
                newRow.eachCell((cell, colNumber) => {
                    if (colNumber > 3 && cell.value) {
                        const cellStr = String(cell.value);
                        const match = cellStr.match(/(\d+)/);
                        if (match && match[0]) {
                            let attendancePercent = Number(match[0]);
                            if (attendancePercent < 80) {
                                cell.fill = {
                                    type: 'pattern',
                                    pattern: 'solid',
                                    fgColor: { argb: 'FBB5AE' },
                                };
                            }
                        }
                    }
                });
            })
            const firstRow = worksheet.getRow(1);
            firstRow.height = 30;
            const buffer = await workbook.xlsx.writeBuffer();
            const todayDate = moment(Date.now()).format("DD_MM_YYYY-HH_mm");
            let fileName = `Attendance-Report${selectedProgram.label}-${todayDate}.xlsx`;
            saveAs(new Blob([buffer]), fileName);
            setDownloadLoading(false);
        }
        catch (error) {
            console.log("Error occurred while downloading excel", error);
            setDownloadLoading(false);
        }
    }

    return (
        <>
            {!loadingComplete ? (
                <Spinner />
            ) : (

                <div className="main-content">
                    <div className="list-wrapper">
                        {projectListData.length > 0 ? (<>
                            <div className="create-program-outer-div">
                                <div className="height-100">
                                    <div className="col-md-6">
                                        <div className="form-wrap">
                                            <label className="form-label-outside" htmlFor="general-information-skills">Project Name</label><span className="text-danger"> *</span>
                                            <div className="form-wrap-inner">
                                                <Select
                                                    placeholder={`Select Project`}
                                                    value={selectedProgram}
                                                    onChange={(event) => changeProjectName(event)}
                                                    options={projectListComponent}
                                                    isSearchable={true}
                                                    isMulti={false}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-wrap">
                                            <label className="form-label-outside" htmlFor="general-information-skills">Select Training Partner</label>
                                            <div className="form-wrap-inner">
                                                <Select
                                                    placeholder={`All Training Partner`}
                                                    value={selectedPartner}
                                                    onChange={(event) => changePartnerData(event)}
                                                    options={partnerListComponent}
                                                    isSearchable={true}
                                                    isMulti={false}
                                                />
                                            </div>
                                        </div>
                                    </div>  
                                </div>
                                <div className="height-100">
                                    <div className="col-md-5"></div>
                                    <div className="col-md-2">
                                        {
                                            downloadLoading === true ? (
                                                <div className="spinner-loader"></div>
                                            ) : (
                                                filteredAttendanceReport && filteredAttendanceReport.length > 0 && (
                                                    <button className="brand-button" onClick={exportToExcel}>Download <i className={EXPORT_ICON} /></button>
                                                )
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                        </>
                        ) : (
                            <p>No projects found.</p>
                        )}
                    </div>
                    {
                        selectedProgram && filteredAttendanceReport.length > 0 ? (
                            <div className="attendance-page-table-outer-wrapper attendance-photo-upload-table-wrapper">
                                <label>Attendance Report</label>
                                <table className="attendance-table-table">
                                    <thead>
                                        <tr className="program-modal-main-row-header">
                                            <th className="serial-no-th-width text-center padding-right-0">Sr No</th>
                                            <th className="width-250px width-responsive-attendance-column padding-left-5">Name</th>
                                            {
                                                totalWeeks && totalWeeks.length > 0 && (
                                                    totalWeeks.map((weekItem) => {
                                                        return (<th className="text-center padding-left-10">{weekItem}</th>);
                                                    })
                                                )
                                            }
                                            <th className="text-center padding-left-10">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            filteredAttendanceReport.map((listItem, index) => {
                                                let rowClass = "";
                                                let below80Color = ""
                                                if (index % 2) {
                                                    rowClass = "program-modal-row-background-color"
                                                }
                                                return (
                                                    <tr className={rowClass} key={`project-attendance-${index}`}>
                                                        <td className="text-center" key={`first-td-${listItem._id}`}>{index + 1}</td>
                                                        <td className="padding-left-5" key={`second-td-${listItem._id}`}>{`${listItem.first_name} ${listItem.last_name}`}</td>
                                                        {
                                                            totalWeeks && totalWeeks.length > 0 && (
                                                                totalWeeks.map((item) => {
                                                                    const repaceText = item.toLowerCase().replace("-", "_");
                                                                    if (listItem[repaceText]) {
                                                                        const weekDataObject = listItem[repaceText];
                                                                        if (weekDataObject.percentage && weekDataObject.percentage < 80) {
                                                                            below80Color = "percentage-below-80-color"
                                                                        }
                                                                        else {
                                                                            below80Color = ""
                                                                        }
                                                                        return (<td key={`week${item}-${listItem._id}`} className={`${below80Color} text-center`}>{weekDataObject.percentage ? `${weekDataObject.percentage} %` : ""}</td>)
                                                                    }
                                                                    else {
                                                                        return (<td></td>)
                                                                    }
                                                                })
                                                            )
                                                        }
                                                        <td className={listItem.totalPresentPercentage && listItem.totalPresentPercentage < 80 ? "percentage-below-80-color text-center" : "text-center"}>{listItem.totalPresentPercentage ? `${listItem.totalPresentPercentage} %` : ""}</td>
                                                    </tr>
                                                )
                                            })
                                        }
                                    </tbody >
                                </table>
                            </div>
                        ) : (
                            <div className="text-center pt-2">
                              <label>No Candidate Found.</label>
                            </div>
                            )
                    }
                </div>
            )}
        </>
    );
};

export default AttendanceReportPage;